
/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { competitorPages, getCompetitorBySlug } from '../../../mocks/competitorPages';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';
import Breadcrumb from '../../../components/feature/Breadcrumb';

export default function VsWarrentonKiaPage() {
  useSEOHead({
    title: 'Rockhound Auto vs Warrenton Kia | Used Cars Warrenton OR | Auto Broker Alternative',
    description: 'Why choose Rockhound Auto over Warrenton Kia? We sell every make and model — not just Kia. Save $3,200–$8,200, zero pressure, fair trade-in values. Serving Warrenton, Astoria, Seaside, and the Oregon Coast.',
    keywords: 'Warrenton Kia alternative, auto broker Warrenton OR, used cars vs Warrenton Kia, Oregon Coast car buying',
    canonicalUrl: 'https://rockhoundauto.com/vs/warrenton-kia',
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const otherCompetitors = competitorPages
    .filter(c => c.slug !== 'warrenton-kia')
    .map(c => c.slug)
    .slice(0, 3);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: 'Why choose Rockhound Auto over Warrenton Kia?', acceptedAnswer: { '@type': 'Answer', text: 'Rockhound Auto gives you access to every vehicle make and model — not just Kia. We save customers $3,200–$8,200 on average with zero pressure, fair trade-in values, and transparent pricing. Serving Warrenton, Astoria, Seaside, and the entire Oregon Coast.' } },
        { '@type': 'Question', name: 'Can Rockhound Auto get me a better deal on a Kia than Warrenton Kia?', acceptedAnswer: { '@type': 'Answer', text: 'Yes! We negotiate with multiple Kia dealers across Oregon and Washington to get you the best price — without the pressure, add-ons, or "this deal expires in 10 minutes" theatrics.' } },
        { '@type': 'Question', name: 'What if I want a Honda, Toyota, or Subaru instead of a Kia?', acceptedAnswer: { '@type': 'Answer', text: "That's exactly the point. Warrenton Kia can only sell you a Kia. Rockhound Auto sources every make and model from dealers across the Pacific Northwest — so you get the right car, not just the one they happen to have on the lot." } },
        { '@type': 'Question', name: 'Does Rockhound Auto serve Warrenton and Astoria?', acceptedAnswer: { '@type': 'Answer', text: "Absolutely. We're based in Warrenton at 1425 SE 9th St and serve Warrenton, Astoria, Seaside, Tillamook, Longview, Kelso, Long Beach, and the entire Pacific Northwest." } }
      ]
    };

    injectJsonLd('faq-schema-wk', schema);

    return () => {
      removeJsonLd('faq-schema-wk');
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <>
      <Breadcrumb 
        items={[
          { label: 'Compare', path: '/compare' },
          { label: 'vs Warrenton Kia' }
        ]} 
      />
      {/* ── HERO ── */}
      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/940c7661715bfdf4a28a232222fb8566.jpg')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/50 to-slate-900/80"></div>

        <div className="relative z-10 container mx-auto px-6 py-28 text-center w-full">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 text-amber-300 px-5 py-2 rounded-full text-sm font-semibold mb-8 tracking-wide uppercase">
            <i className="ri-scales-3-line"></i> Honest Comparison
          </div>

          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
            Warrenton Kia Sells One Brand.<br />
            <span className="text-emerald-400">We Sell Freedom.</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-4 leading-relaxed">
            Kia makes great cars. But what if you want a Honda? A Toyota? A Subaru?<br />
            <strong className="text-white">At Warrenton Kia, you'd need to visit a different lot.</strong>
          </p>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            Rockhound Auto brokers every make and model across Oregon &amp; Washington — with zero pressure, transparent pricing, and savings that average <strong className="text-emerald-400">$3,200–$8,200</strong> per deal.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#consultation" className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap shadow-lg cursor-pointer">
              Get Free Savings Estimate
            </a>
            <a href="tel:+18554686348" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call Us
            </a>
          </div>
        </div>
      </section>

      {/* ── THE SINGLE-BRAND DILEMMA ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">The Single-Brand Dilemma</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              Here's what happens when you walk into a single-brand dealership with an open mind.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-map-pin-2-line',
                step: '01',
                title: 'You arrive wanting options',
                body: "You've done your research. You're open to a Honda CR-V, a Toyota RAV4 — maybe a Subaru Forester. You just want the best deal for your family.",
                color: 'text-slate-700',
              },
              {
                icon: 'ri-store-2-line',
                step: '02',
                title: 'The lot has… Kias',
                body: `"We have the Sportage, the Sorento, the Telluride…" Great cars. But you wanted to compare across brands. That's not an option here.`,
                color: 'text-amber-600',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                step: '03',
                title: 'You leave with a Kia (or nothing)',
                body: "Either you settle for what's on the lot, or you drive home empty-handed after 3 hours. Neither is ideal. There's a better way.",
                color: 'text-red-600',
              },
            ].map((item) => (
              <div key={item.step} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-md transition">
                <div className="text-5xl font-black text-slate-100 mb-2">{item.step}</div>
                <div className={`w-12 h-12 flex items-center justify-center mb-4 ${item.color}`}>
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-800">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <p className="text-2xl font-bold text-emerald-800 mb-2">The Rockhound Way</p>
            <p className="text-emerald-700 text-lg">
              Tell us what you want. We source it from <strong>every dealer across Oregon &amp; Washington</strong>, negotiate the best price, and deliver it to you. No lot. No pressure. No single-brand limitation.
            </p>
          </div>
        </div>
      </section>

      {/* ── KNOW BEFORE YOU GO ── */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-lightbulb-line"></i> Buyer Education
            </div>
            <h2 className="text-4xl font-bold mb-4">Know Before You Go</h2>
            <p className="text-slate-400 text-lg">
              Common tactics you'll encounter at single-brand dealerships — and how to spot them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-timer-flash-line',
                title: '"This Deal Expires in 10 Minutes"',
                reality: 'The urgency is manufactured to prevent you from shopping around.',
                tip: '💡 Take your time. The deal will still be there tomorrow — often better, because they need to hit their monthly quota.',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'The Warranty You Didn\'t Ask For',
                reality: 'Paint protection, fabric coating, gap insurance — all "bundled in" before you even sit down.',
                tip: '💡 Ask for an itemized breakdown. Everything should be optional, not mandatory.',
              },
              {
                icon: 'ri-exchange-dollar-line',
                title: 'The Trade‑In Lowball',
                reality: 'Single dealers offer one price. No competition means no incentive to offer fair value.',
                tip: '💡 Get multiple offers. Competition drives fair pricing — that\'s how we get you $3,000–$5,000 more.',
              },
              {
                icon: 'ri-calculator-line',
                title: 'The Monthly Payment Shuffle',
                reality: 'Stretching your loan to 84 months hides the real cost of the vehicle.',
                tip: '💡 Always ask for the total price, interest rate, and loan term — not just the monthly payment.',
              },
            ].map((tactic) => (
              <div key={tactic.title} className="bg-slate-800 rounded-2xl p-8 border border-slate-700 hover:border-emerald-500/50 transition">
                <div className="w-12 h-12 flex items-center justify-center mb-4 text-amber-400">
                  <i className={`${tactic.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-1">{tactic.title}</h3>
                <p className="text-slate-400 text-sm mb-3">{tactic.reality}</p>
                <p className="text-emerald-300 text-sm leading-relaxed">{tactic.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAIR FIGHT COMPARISON TABLE ── */}
      <section id="comparison" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">The Fair Fight</h2>
            <p className="text-slate-500 text-lg">No spin. Just the facts.</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-5 text-left font-semibold text-slate-600 border-b border-slate-200 text-sm uppercase tracking-wide">What Matters</th>
                  <th className="p-5 text-center font-bold text-emerald-700 border-b border-emerald-200 bg-emerald-50 text-sm uppercase tracking-wide">Rockhound Auto</th>
                  <th className="p-5 text-center font-semibold text-slate-500 border-b border-slate-200 text-sm uppercase tracking-wide">Warrenton Kia</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Vehicle Selection', 'Every make & model', 'Kia only'],
                  ['Pricing Transparency', 'Fully itemized, no surprises', 'Fees appear at signing'],
                  ['Sales Pressure', 'Zero. Take your time.', 'Time-limited offers'],
                  ['Trade‑In Value', 'Fair market, multiple offers', 'Single offer'],
                  ['Inventory Access', 'OR + WA dealer network', "What's on the lot"],
                  ['Negotiation', 'We negotiate for you', 'You negotiate alone'],
                  ['Finance Rate', 'Competitive, transparent', 'May include markup'],
                  ['Add‑Ons', 'Your choice, always', 'Often pre-installed'],
                ].map(([feature, us, them], i) => (
                  <tr key={feature} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="p-4 font-medium text-slate-700 border-b border-slate-100">{feature}</td>
                    <td className="p-4 text-center bg-emerald-50/60 border-b border-emerald-100">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-check-line text-emerald-600 text-lg"></i>
                        <span className="font-semibold text-emerald-800 text-sm">{us}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center border-b border-slate-100">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-close-line text-red-400 text-lg"></i>
                        <span className="text-slate-500 text-sm">{them}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── PRICING BREAKDOWN ── */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">Real Numbers. Real Savings.</h2>
            <p className="text-slate-500 text-lg">2024 Kia Sportage SX Prestige AWD — same vehicle, very different price.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Warrenton Kia */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800">Warrenton Kia</h3>
                <p className="text-slate-500 text-sm mt-1">Traditional single-brand dealership</p>
              </div>
              <div className="space-y-3">
                {[
                  ['MSRP', '$38,490', false],
                  ['Dealer Markup', '+$2,995', true],
                  ['Doc Fee', '+$599', true],
                  ['Paint Protection (mandatory)', '+$1,295', true],
                  ['Fabric Coating (mandatory)', '+$695', true],
                  ['Extended Warranty Push', '+$2,200', true],
                ].map(([label, amount, isFee]) => (
                  <div key={label as string} className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600 text-sm">{label as string}</span>
                    <span className={`font-semibold text-sm ${isFee ? 'text-red-500' : 'text-slate-800'}`}>{amount as string}</span>
                  </div>
                ))}
                <div className="flex justify-between py-3 bg-slate-100 px-4 rounded-xl mt-2">
                  <span className="font-bold">You Pay</span>
                  <span className="font-black text-2xl text-slate-800">$46,274</span>
                </div>
              </div>
            </div>

            {/* Rockhound */}
            <div className="bg-slate-900 rounded-2xl shadow-xl border-2 border-emerald-500 p-8 text-white relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-6 py-1.5 rounded-full font-bold text-sm whitespace-nowrap shadow">
                YOU SAVE $8,124
              </div>
              <div className="text-center mb-6 mt-2">
                <h3 className="text-2xl font-bold">Rockhound Auto</h3>
                <p className="text-slate-400 text-sm mt-1">Auto broker — we work for you</p>
              </div>
              <div className="space-y-3">
                {[
                  ['MSRP', '$38,490', false],
                  ['Negotiated Discount', '−$3,630', false, 'text-emerald-400'],
                  ['Broker Fee', '+$500', false],
                  ['Doc Fee', '+$150', false],
                  ['Mandatory Add‑Ons', '$0', false, 'text-emerald-400'],
                  ['Surprise Warranties', '$0', false, 'text-emerald-400'],
                ].map(([label, amount, , colorClass]) => (
                  <div key={label as string} className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-slate-300 text-sm">{label as string}</span>
                    <span className={`font-semibold text-sm ${colorClass || 'text-white'}`}>{amount as string}</span>
                  </div>
                ))}
                <div className="flex justify-between py-3 bg-emerald-600 px-4 rounded-xl mt-2">
                  <span className="font-bold">You Pay</span>
                  <span className="font-black text-2xl">$38,150</span>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            * Pricing examples are illustrative based on real market data. Your actual savings will vary by vehicle and market conditions.
          </p>
        </div>
      </section>

      {/* ── WHAT WE DON'T HAVE (AND WHY THAT'S GOOD) ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">What We Don't Have<br /><span className="text-emerald-600">(And Why That's Good)</span></h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              Yes, Warrenton Kia has things we don't. Let's be honest about all of them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-tools-line',
                theyHave: 'A Service Department',
                weHave: 'Your money still in your wallet',
                body: 'Their service dept is great for maintenance and repairs. We help you buy a reliable vehicle so you spend less time at the service counter and more time on the road.',
              },
              {
                icon: 'ri-car-line',
                theyHave: 'Brand New Kias',
                weHave: 'Every brand, new or used, at better prices',
                body: 'New cars are wonderful. They also depreciate $8,000–$12,000 the moment you leave the lot. We find you the sweet spot — low miles, full warranty, fraction of the depreciation hit.',
              },
              {
                icon: 'ri-settings-3-line',
                theyHave: 'A Parts Department',
                weHave: 'No parts upsells on your way out',
                body: 'We don\'t have a parts counter, which means we have no incentive to recommend parts or services you don\'t need. Your mechanic, your choice.',
              },
              {
                icon: 'ri-building-2-line',
                theyHave: 'A Big Shiny Showroom',
                weHave: 'Lower overhead = lower prices for you',
                body: 'That gorgeous showroom with the espresso machine and the 60‑inch screens costs money to maintain. Our overhead is lean. That savings goes directly into your deal.',
              },
            ].map((item) => (
              <div key={item.theyHave} className="rounded-2xl border border-slate-200 p-8 hover:shadow-md transition">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl text-slate-500 shrink-0">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-0.5">They have</p>
                    <p className="font-bold text-slate-800">{item.theyHave}</p>
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2 mb-4">
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-0.5">We have</p>
                  <p className="font-bold text-emerald-800">{item.weHave}</p>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">Real People. Real Savings.</h2>
            <p className="text-slate-500 text-lg">No actors. No stock photos. Just folks from around here.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Jennifer M.',
                location: 'Astoria, OR',
                saved: '$6,800',
                vehicle: 'Kia Telluride',
                quote: 'I was at Warrenton Kia, pen in hand, about to sign for $52,000. My husband said "call Rockhound first." Best phone call I ever made. Same Telluride, $45,200. We used the difference to finally fix the deck.',
              },
              {
                name: 'David R.',
                location: 'Seaside, OR',
                saved: '$3,700 on trade‑in alone',
                vehicle: 'Honda CR‑V',
                quote: 'Warrenton Kia offered me $8,500 for my trade. Rockhound got me $12,200 — and then found me a Honda CR‑V because, turns out, I wanted to compare across brands. Great experience.',
              },
              {
                name: 'Maria S.',
                location: 'Longview, WA',
                saved: '$4,200',
                vehicle: 'Kia Sorento',
                quote: 'They said the deal expired that day. I left, called Rockhound, and got the same Sorento for $4,200 less with zero drama. Turns out the deal didn\'t expire after all.',
              },
            ].map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 flex flex-col">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-slate-700 italic leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-bold text-slate-800">{t.name}</p>
                  <p className="text-slate-500 text-sm">{t.location} · {t.vehicle}</p>
                  <p className="text-emerald-600 font-bold mt-1">Saved: {t.saved}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONSULTATION FORM ── */}
      <section id="consultation" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Skip the Showroom Theater.</h2>
            <p className="text-slate-500 text-lg">
              Just tell us what you want — we'll find it and tell you exactly what you'll save.
            </p>
          </div>

          <form
            id="warrenton-kia-savings-form"
            action="https://readdy.ai/api/form/d6f9ieor225iqdhs04t0"
            method="POST"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-slate-50 rounded-2xl p-8 shadow-sm border border-slate-200"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">First Name</label>
                <input type="text" name="first_name" required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Last Name</label>
                <input type="text" name="last_name" required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Email</label>
                <input type="email" name="email" required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Phone</label>
                <input type="tel" name="phone" required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-slate-700 font-semibold mb-2 text-sm">What vehicle are you looking for?</label>
              <input
                type="text"
                name="vehicle_interest"
                placeholder="e.g., Kia Telluride, Honda CR‑V, Toyota RAV4 — anything goes"
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-emerald-500 text-sm bg-white"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Trade‑in?</label>
                <select name="has_trade_in" className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-emerald-500 text-sm bg-white cursor-pointer">
                  <option value="no">No trade‑in</option>
                  <option value="yes">Yes, I have a trade‑in</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-2 text-sm">Your Location</label>
                <select name="location" className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-emerald-500 text-sm bg-white cursor-pointer">
                  <option value="warrenton">Warrenton, OR</option>
                  <option value="astoria">Astoria, OR</option>
                  <option value="seaside">Seaside, OR</option>
                  <option value="longview">Longview, WA</option>
                  <option value="kelso">Kelso, WA</option>
                  <option value="tillamook">Tillamook, OR</option>
                  <option value="cannon-beach">Cannon Beach, OR</option>
                  <option value="long-beach">Long Beach, WA</option>
                  <option value="other">Somewhere else</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-slate-700 font-semibold mb-2 text-sm">Anything else? (Optional)</label>
              <textarea
                name="additional_details"
                rows={3}
                maxLength={500}
                placeholder="Budget, timeline, specific trim, color preference — whatever helps us help you."
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-emerald-500 text-sm bg-white resize-none"
              ></textarea>
            </div>

            {formStatus === 'success' && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-xl text-sm">
                <i className="ri-check-circle-line mr-2"></i>Got it! We'll be in touch within 2 hours during business hours.
              </div>
            )}
            {formStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 text-red-700 rounded-xl text-sm">
                <i className="ri-error-warning-line mr-2"></i>Something went wrong. Please try again or just call us.
              </div>
            )}

            <button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-4 rounded-xl transition text-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {formStatus === 'submitting' ? 'Sending…' : 'Get My Free Savings Estimate'}
            </button>
            <p className="text-center text-slate-400 text-xs mt-3">We respond within 2 hours. No spam. No pressure.</p>
          </form>
        </div>
      </section>

      {/* ── OTHER COMPARISONS ── */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-5xl">
          {/* Content for other comparisons goes here */}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            One brand or every brand.<br />
            <span className="text-emerald-400">Your call.</span>
          </h2>
          <p className="text-xl text-slate-300 mb-10 leading-relaxed">
            Warrenton Kia is a fine dealership. But if you want options, transparency, and someone negotiating <em>for</em> you — that's us.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#consultation" className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap">
              Get Free Savings Estimate
            </a>
            <a href="tel:+18554686348" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>Call Us
            </a>
          </div>
        </div>
      </section>

    </>
  );
}
